import React, {ChangeEvent} from 'react';
import {useFieldArray, useWatch} from 'react-hook-form';
import type {useForm} from 'react-hook-form';
import cloneDeep from 'lodash/cloneDeep';

import I18N from 'assets/i18n/brandlift-project';

import {BordomatikBox, CreativeBox} from 'client/common/types';

import Block from 'client/common/components/block';
import BlockInput from 'client/common/components/block-input';
import SideBlock from 'client/common/components/side-block';

import {setImmut, OptionLists} from './utils';

const i18n = I18N.creatives.creativeSettings;
const i18nAdapted = I18N.creatives;

interface BrandUniqueBlockProps {
    box: CreativeBox | BordomatikBox;
    disabled: boolean;
    register: ReturnType<typeof useForm>['register'];
    errors: ReturnType<typeof useForm>['errors'];
    control: ReturnType<typeof useForm>['control'];
    updateSurveyBoxContext: (box: unknown) => Promise<void>;
}

export default function BrandUniqueBlock(props: BrandUniqueBlockProps) {
    const {box, disabled, register, errors, control, updateSurveyBoxContext} = props;

    const {fields: aboutFields} = useFieldArray({name: 'options.about'});
    const {fields: imageFields} = useFieldArray({name: 'options.image'});
    const {fields: brandFields} = useFieldArray({name: 'options.brand'});

    const brandNameValue = useWatch<string>({
        control,
        name: 'brandName'
    });

    const brandFieldsOptions = [
        ...brandFields,
        {
            id: '-1',
            text: brandNameValue,
            disabled: true
        }
    ];

    const handleAboutOptionsBlur = (index: number) => async (e: ChangeEvent<HTMLInputElement>) => {
        const texts = cloneDeep(box?.context?.options?.about ?? ['', '', '', '', '']);

        texts[index] = e.target.value;

        await updateSurveyBoxContext(setImmut(box, 'context.options.about', texts));
    };

    const handleImageOptionsBlur = (index: number) => async (e: ChangeEvent<HTMLInputElement>) => {
        const texts = cloneDeep(box?.context?.options?.image ?? ['', '', '', '', '']);

        texts[index] = e.target.value;

        await updateSurveyBoxContext(setImmut(box, 'context.options.image', texts));
    };

    const handleBrandOptionsBlur = (index: number) => async (e: ChangeEvent<HTMLInputElement>) => {
        const texts = cloneDeep((box as BordomatikBox)?.context?.options?.brand ?? ['', '']);

        texts[index] = e.target.value;

        await updateSurveyBoxContext(setImmut(box, 'context.options.brand', texts));
    };

    return (
        <Block
            header={i18nAdapted[`${box.name}Settings`].new.unique.label}
            description={i18nAdapted[`${box.name}Settings`].new.unique.desc}
        >
            {box.name === 'bordomatik' && (
                <BlockInput
                    title={i18n.questionText.brand}
                    description={i18n.questionText.brandDesc}
                >
                    <OptionLists
                        register={register}
                        errors={errors}
                        rules={{
                            validate: (v: string) =>
                                v.trim() ? true : i18n.optionValidationMsg.brand
                        }}
                        fields={brandFieldsOptions}
                        fieldName='options.brand'
                        disabled={disabled}
                        onBlur={handleBrandOptionsBlur}
                        minItems={2}
                        maxItems={2}
                        hasNoOpinion
                    />
                </BlockInput>
            )}
            <BlockInput title={i18n.questionText.about}>
                <OptionLists
                    register={register}
                    errors={errors}
                    rules={{
                        validate: (v: string) => (v.trim() ? true : i18n.optionValidationMsg.about)
                    }}
                    fields={aboutFields}
                    fieldName='options.about'
                    disabled={disabled}
                    onBlur={handleAboutOptionsBlur}
                    hasNoOpinion
                />
            </BlockInput>
            <BlockInput title={i18n.questionText.image} description={i18n.questionText.imageDesc}>
                <OptionLists
                    register={register}
                    errors={errors}
                    rules={{
                        validate: (v: string) => (v.trim() ? true : i18n.optionValidationMsg.image)
                    }}
                    fields={imageFields}
                    fieldName='options.image'
                    disabled={disabled}
                    onBlur={handleImageOptionsBlur}
                    hasNoOpinion
                />
            </BlockInput>
            <SideBlock
                imageSet='video'
                text={i18nAdapted[`${box.name}Settings`].sideBlock.brandUniqueText}
                link={i18nAdapted[`${box.name}Settings`].sideBlock.brandUniqueLink}
            />
        </Block>
    );
}
